import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto, LocationDto } from '@/types/organization';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { CreateStockAdjustmentRequest, StockAdjustmentDto, StockAdjustmentLineInput, UpdateStockAdjustmentRequest } from '@/types/adjustments';
import BarcodeAdjustmentPicker from './BarcodeAdjustmentPicker';
import StockStatusChangeDialog from './StockStatusChangeDialog';

const REASONS = ['Damage', 'Loss', 'Found', 'Correction', 'Other'];

// batchNo/clientName/labelQty/materialCode are display-only (resolved via barcode scan, since
// there's no manual batch/client picker on this screen) — stripped before the line is sent to the
// API, which only needs batchId (ClientId is re-resolved server-side from the matched on-hand row,
// not sent by the client) and, under Barcode Required, scannedBarcode.
type FormLine = StockAdjustmentLineInput & {
  batchNo?: string | null;
  clientName?: string | null;
  labelQty?: number | null;
  materialCode?: string | null;
  locationCode?: string | null;
  onHand?: boolean;
};

const emptyLine: FormLine = { materialId: '', locationId: null, batchId: null, batchNo: null, adjustedQty: 0, remarks: null, clientName: null };

export default function StockAdjustmentsPage() {
  const { t } = useTranslation('stockAdjustment');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: adjustments = [], isLoading } = useQuery({
    queryKey: ['stock-adjustments'],
    queryFn: async () => (await apiClient.get<StockAdjustmentDto[]>('/stock-adjustments')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  // Barcode Required turns this screen barcode-wise: nothing can be adjusted without scanning the
  // label on the physical unit, and each line then represents that one unit. Off, the screen keeps
  // its original row-wise behaviour. Server-side StockAdjustmentService enforces the same rule.
  const { data: processConfig } = useProcessConfiguration();
  const barcodeMandatory = processConfig?.barcodeMandatory ?? false;

  const [open, setOpen] = useState(false);
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState('Correction');
  const [scanError, setScanError] = useState<string | null>(null);

  // /locations requires warehouseId — omitting it silently binds to Guid.Empty and returns [].
  // Scope the fetch to the currently selected warehouse instead.
  const { data: locations = [] } = useQuery({
    queryKey: ['locations', warehouseId],
    queryFn: async () => (await apiClient.get<LocationDto[]>('/locations', { params: { warehouseId } })).data,
    enabled: Boolean(warehouseId),
  });
  const [lines, setLines] = useState<FormLine[]>([{ ...emptyLine }]);

  const warehouseLocations = locations;

  // Barcode-wise the blank placeholder line has no meaning — only actually-scanned units are real.
  const scannedLines = lines.filter((l) => l.scannedBarcode);

  const resetForm = () => {
    setEditingId(null);
    setWarehouseId('');
    setReason('Correction');
    setScanError(null);
    setLines([{ ...emptyLine }]);
  };

  // Only usable while Status is still "Draft" — mirrors StockAdjustmentService.UpdateAsync's guard.
  const openEdit = (r: StockAdjustmentDto) => {
    setEditingId(r.id);
    setWarehouseId(r.warehouseId);
    setReason(r.reason);
    setScanError(null);
    setLines(r.lines.map((l) => ({
      materialId: l.materialId, locationId: l.locationId, batchId: l.batchId, batchNo: l.batchNo,
      adjustedQty: l.adjustedQty, remarks: l.remarks, clientName: l.clientName,
      // Carried back so an edit re-sends the same scan rather than tripping the server's
      // "must be scanned" guard on a draft that was already scanned once.
      scannedBarcode: l.scannedBarcode, materialCode: l.materialCode, locationCode: l.locationCode,
      labelQty: l.currentQty,
    })));
    setOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines: StockAdjustmentLineInput[] = lines
        // Barcode-wise, only scanned units count; row-wise, any line with a material chosen.
        .filter((l) => (barcodeMandatory ? Boolean(l.scannedBarcode) : Boolean(l.materialId)))
        .map(({
          batchNo: _batchNo, clientName: _clientName, labelQty: _labelQty,
          materialCode: _materialCode, locationCode: _locationCode, onHand: _onHand, ...line
        }) => line);
      if (editingId) {
        const payload: UpdateStockAdjustmentRequest = { warehouseId, reason, lines: cleanLines };
        return apiClient.put(`/stock-adjustments/${editingId}`, payload);
      }
      const payload: CreateStockAdjustmentRequest = { warehouseId, reason, lines: cleanLines };
      return apiClient.post('/stock-adjustments', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] });
      setOpen(false);
    },
  });

  const commitMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/stock-adjustments/${id}/commit`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stock-adjustments'] }),
  });

  const columns: DataTableColumn<StockAdjustmentDto>[] = [
    { field: 'adjustmentNumber', headerName: t('table.number') },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'reason', headerName: t('table.reason'), filterable: true },
    { field: 'adjustmentDate', headerName: t('table.date'), render: (r) => formatDate(r.adjustmentDate) },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <Stack direction="row" spacing={1}>
          {r.status === 'Draft' && (
            <IfPermitted permission={PERMISSIONS.STOCK_ADJUSTMENT_MANAGE}>
              <Button size="small" onClick={() => openEdit(r)}>
                {t('edit')}
              </Button>
            </IfPermitted>
          )}
          <IfPermitted permission={PERMISSIONS.STOCK_ADJUSTMENT_APPROVE}>
            {r.status === 'Draft' && (
              <Button size="small" variant="outlined" disabled={commitMutation.isPending} onClick={() => commitMutation.mutate(r.id)}>
                {t('commit')}
              </Button>
            )}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.STOCK_ADJUSTMENT_MANAGE}>
            <Stack direction="row" spacing={1}>
              {/* Reclassifying stock is an adjustment-class correction, so it lives here rather
                  than in its own nav entry — same permission, same screen. */}
              <Button variant="outlined" onClick={() => setStatusChangeOpen(true)}>
                {t('statusChange.action')}
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpen(true); }}>
                {t('add')}
              </Button>
            </Stack>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={adjustments} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="stock-adjustments" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <SearchableSelect
                label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
                options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
              />
              <TextField select label={t('form.reason')} value={reason} onChange={(e) => setReason(e.target.value)} fullWidth>
                {REASONS.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </TextField>
            </Stack>

            {barcodeMandatory ? (
              <BarcodeAdjustmentPicker
                warehouseId={warehouseId}
                materials={materials}
                locations={warehouseLocations}
                lines={lines}
                setLines={setLines}
                error={scanError}
                setError={setScanError}
              />
            ) : (
              <>
                {/* No scan field here: with Barcode Required off, adjustment is row-wise and the
                    material/location/batch pickers below are the whole input. Scanning only appears
                    in the barcode-wise branch above, where a line IS a scanned physical unit. */}
                {lines.map((line, idx) => (
                  <GridContainer key={idx} spacing={1.5} columns={12}>
                    <GridItem xs={12} sm={4}>
                      <SearchableSelect
                        label={t('form.material')} value={line.materialId} size="small" fullWidth
                        onChange={(materialId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId, batchId: null, batchNo: null } : l)))}
                        options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                      />
                      {line.batchNo ? (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                          {t('form.resolvedBatch', { batchNo: line.batchNo })}
                        </Typography>
                      ) : undefined}
                      {line.clientName ? (
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5, display: 'block' }}>
                          {t('form.resolvedClient', { clientName: line.clientName })}
                        </Typography>
                      ) : undefined}
                    </GridItem>
                    <GridItem xs={6} sm={2}>
                      <SearchableSelect
                        label={t('form.location')} value={line.locationId ?? ''} size="small" fullWidth
                        onChange={(locationId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, locationId: locationId || null } : l)))}
                        options={[{ value: '', label: t('form.none') }, ...warehouseLocations.map((loc) => ({ value: loc.id, label: loc.code }))]}
                      />
                    </GridItem>
                    <GridItem xs={6} sm={2}>
                      <TextField
                        type="number" label={t('form.adjustedQty')} value={line.adjustedQty} size="small" fullWidth
                        onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, adjustedQty: Number(e.target.value) } : l)))}
                      />
                    </GridItem>
                    <GridItem xs={10} sm={3}>
                      <TextField
                        label={t('form.remarks')} value={line.remarks ?? ''} size="small" fullWidth
                        onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, remarks: e.target.value || null } : l)))}
                      />
                    </GridItem>
                    <GridItem xs={2} sm={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </GridItem>
                  </GridContainer>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...emptyLine }])} sx={{ alignSelf: 'flex-start' }}>
                  {t('form.addLine')}
                </Button>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={
              !warehouseId || createMutation.isPending
              || (barcodeMandatory ? scannedLines.length === 0 : lines.some((l) => !l.materialId))
            }
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <StockStatusChangeDialog open={statusChangeOpen} onClose={() => setStatusChangeOpen(false)} />
    </>
  );
}
